import { useEffect, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Wallet, TrendingUp, Clock, Download, FileSpreadsheet } from "lucide-react";
import api from "@/lib/api";
import { euro, euro0, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/DateInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { downloadElementPdf } from "@/lib/pdf";
import { useSettings } from "@/lib/useSettings";

const QUARTERS = [
  { v: "1", l: "T1 (Gen–Mar)" },
  { v: "2", l: "T2 (Apr–Giu)" },
  { v: "3", l: "T3 (Lug–Set)" },
  { v: "4", l: "T4 (Ott–Dic)" },
];

// Admin income report: invoiced vs collected, filter by year / quarter / date range, PDF & CSV export.
export default function IncomeReport() {
  const [data, setData] = useState(null);
  const settings = useSettings();
  const azienda = settings?.ragione_sociale || "";
  const [mode, setMode] = useState("anno");
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = useCallback(() => {
    let qs = `year=${year}`;
    if (mode === "trimestre") qs = `year=${year}&quarter=${quarter}`;
    else if (mode === "intervallo") {
      if (!start || !end) return;
      qs = `start=${start}&end=${end}`;
    }
    api.get(`/reports/income?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [mode, year, quarter, start, end]);
  useEffect(() => { load(); }, [load]);

  const handleMode = (m) => {
    setMode(m);
    if (m === "intervallo" && (!start || !end)) {
      setStart(`${year}-01-01`);
      setEnd(new Date().toISOString().slice(0, 10));
    }
  };

  if (!data) return null;

  const periodLabel = mode === "trimestre" ? `T${quarter} ${year}`
    : mode === "intervallo" ? `${fmtDate(start)} – ${fmtDate(end)}`
    : `Anno ${data.year}`;
  const fileLabel = mode === "trimestre" ? `T${quarter}-${year}`
    : mode === "intervallo" ? `${start}_${end}`
    : `${data.year}`;

  const Delta = ({ pct }) => {
    if (pct == null) return <span className="text-xs text-muted-foreground" data-testid="income-delta">vs {data.previous?.label}: n/d</span>;
    const up = pct >= 0;
    return (
      <span className={`text-xs font-semibold ${up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="income-delta">
        {up ? "▲" : "▼"} {Math.abs(pct)}% vs {data.previous?.label}
      </span>
    );
  };

  const exportCsv = () => {
    const rows = [["N. Fattura", "Data", "Cliente", "Totale", "Stato"]];
    data.detail.forEach((d) => rows.push([d.numero, fmtDate(d.data), (d.cliente_nome || "").replace(/;/g, ","), String(d.totale).replace(".", ","), d.stato === "pagata" ? "Pagata" : "Non pagata"]));
    rows.push([]);
    rows.push(["", "", "Fatturato", String(data.totals.fatturato).replace(".", ","), ""]);
    rows.push(["", "", "Incassato", String(data.totals.incassato).replace(".", ","), ""]);
    rows.push(["", "", "Da incassare", String(data.totals.da_incassare).replace(".", ","), ""]);
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Report-Incassi-${fileLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="income-report">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold">Report Incassi</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={mode} onValueChange={handleMode}>
            <SelectTrigger className="w-36" data-testid="income-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="anno">Anno intero</SelectItem>
              <SelectItem value="trimestre">Trimestre</SelectItem>
              <SelectItem value="intervallo">Intervallo date</SelectItem>
            </SelectContent>
          </Select>

          {mode !== "intervallo" && (
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="income-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.available_years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {mode === "trimestre" && (
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="w-36" data-testid="income-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUARTERS.map((q) => <SelectItem key={q.v} value={q.v}>{q.l}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {mode === "intervallo" && (
            <>
              <DateInput value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="income-start-input" />
              <span className="text-muted-foreground text-sm">–</span>
              <DateInput value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="income-end-input" />
            </>
          )}

          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="income-export-csv"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> CSV</Button>
          <Button size="sm" onClick={() => downloadElementPdf("income-report-print", `Report-Incassi-${fileLabel}.pdf`)} data-testid="income-export-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-5">
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" /> Fatturato · {periodLabel}</div>
          <div className="font-heading text-2xl font-extrabold mt-1" data-testid="income-total-fatturato">{euro0(data.totals.fatturato)}</div>
          <div className="mt-1"><Delta pct={data.variazione?.fatturato} /></div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400"><Wallet className="w-4 h-4" /> Incassato</div>
          <div className="font-heading text-2xl font-extrabold mt-1 text-emerald-600 dark:text-emerald-400" data-testid="income-total-incassato">{euro0(data.totals.incassato)}</div>
          <div className="mt-1"><Delta pct={data.variazione?.incassato} /></div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400"><Clock className="w-4 h-4" /> Da incassare</div>
          <div className="font-heading text-2xl font-extrabold mt-1 text-amber-600 dark:text-amber-400" data-testid="income-total-daincassare">{euro0(data.totals.da_incassare)}</div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data.months} margin={{ left: -10, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="mese" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
          <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 13 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="fatturato" name="Fatturato" fill="#94a3b8" radius={[6, 6, 0, 0]} />
          <Bar dataKey="incassato" name="Incassato" fill="#EA580C" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* Off-screen printable report used only for PDF export */}
      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <div id="income-report-print" data-no-translate className="bg-white text-slate-900" style={{ width: 760, padding: 40 }}>
          <div className="flex justify-between items-center pb-4 mb-5" style={{ borderBottom: "3px solid #E1352A" }}>
            <div>
              <div className="text-xl font-extrabold uppercase" style={{ color: "#E1352A" }}>{azienda}</div>
              <div className="text-sm text-slate-500 mt-1">Report Incassi — {periodLabel}</div>
            </div>
            {settings?.logo_url && <img src={settings.logo_url} alt="logo" crossOrigin="anonymous" style={{ maxHeight: 54, maxWidth: 170, objectFit: "contain" }} />}
          </div>
          <div className="flex gap-6 mb-5 text-sm">
            <div>Fatturato: <strong>{euro(data.totals.fatturato)}</strong></div>
            <div>Incassato: <strong>{euro(data.totals.incassato)}</strong></div>
            <div>Da incassare: <strong>{euro(data.totals.da_incassare)}</strong></div>
          </div>
          <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#E1352A", color: "#fff" }}>
                <th className="text-left py-1.5 px-2">Periodo</th>
                <th className="text-right py-1.5 px-2">Fatturato</th>
                <th className="text-right py-1.5 px-2">Incassato</th>
                <th className="text-right py-1.5 px-2">Da incassare</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td className="py-1.5 px-2">{m.mese}</td>
                  <td className="py-1.5 px-2 text-right">{euro(m.fatturato)}</td>
                  <td className="py-1.5 px-2 text-right">{euro(m.incassato)}</td>
                  <td className="py-1.5 px-2 text-right">{euro(m.da_incassare)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #E1352A", fontWeight: 700 }}>
                <td className="py-2 px-2">TOTALE</td>
                <td className="py-2 px-2 text-right">{euro(data.totals.fatturato)}</td>
                <td className="py-2 px-2 text-right">{euro(data.totals.incassato)}</td>
                <td className="py-2 px-2 text-right">{euro(data.totals.da_incassare)}</td>
              </tr>
            </tbody>
          </table>
          <div className="text-center text-[10px] text-slate-400 mt-8">{azienda ? `Gestionale di ${azienda}` : "K Gestionale"}</div>
        </div>
      </div>
    </div>
  );
}
